import axios, { AxiosInstance, AxiosError } from 'axios';
import type { APIError } from '../types';
import { installRefreshOn401 } from './sessionRefresh';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

class APIClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 seconds
    });
    // V17.534 (RBAC 2A): a 401 renews the access token once and re-sends once (sessionRefresh).
    installRefreshOn401(this.client);

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add JWT token if available
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        // Add API key if available
        const apiKey = localStorage.getItem('api_key');
        if (apiKey) {
          config.headers['X-API-Key'] = apiKey;
        }

        // Add QuickBooks realm ID if available
        const realmId = localStorage.getItem('quickbooks_realm_id');
        if (realmId) {
          config.headers['X-Realm-ID'] = realmId;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<APIError>) => {
        // Handle rate limiting
        if (error.response?.status === 429) {
          const retryAfter = error.response.headers['retry-after'];
          console.warn(`Rate limited. Retry after ${retryAfter}s`);

          // Show user-friendly message
          this.handleError({
            error_code: 'RATE_LIMIT_EXCEEDED',
            message: `Too many requests. Please wait ${retryAfter} seconds.`,
            timestamp: new Date().toISOString(),
          });
        }

        // Handle authentication errors
        if (error.response?.status === 401) {
          console.error('Authentication failed');
          // Could redirect to login or show auth modal
        }

        // Handle server errors
        if (error.response?.status && error.response.status >= 500) {
          this.handleError({
            error_code: 'SERVER_ERROR',
            message: 'Server error. Please try again later.',
            timestamp: new Date().toISOString(),
          });
        }

        return Promise.reject(error);
      }
    );
  }

  private handleError(error: APIError) {
    // This could be enhanced to show toast notifications
    console.error('API Error:', error);
  }

  async get<T>(endpoint: string, params?: Record<string, unknown>, config?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get<T>(endpoint, { params, ...config });
    // V9.5 P4 FIX: For blob responses, return the entire response object so caller can access response.data
    if (config?.responseType === 'blob') {
      return response as unknown as T;
    }
    return response.data;
  }

  async post<T>(endpoint: string, data?: unknown, config?: Record<string, unknown>): Promise<T> {
    const response = await this.client.post<T>(endpoint, data, config);
    // V17.28: For blob responses, return the entire response object so caller can access response.data and headers
    if (config?.responseType === 'blob') {
      return response as unknown as T;
    }
    return response.data;
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    console.log('🔵 [APIClient] PUT request to:', endpoint);
    console.log('🔵 [APIClient] PUT data:', data);
    console.log('🔵 [APIClient] Base URL:', API_BASE_URL);

    try {
      const response = await this.client.put<T>(endpoint, data);
      console.log('🟢 [APIClient] PUT response status:', response.status);
      console.log('🟢 [APIClient] PUT response data:', response.data);
      return response.data;
    } catch (error) {
      console.error('🔴 [APIClient] PUT request failed:', error);
      throw error;
    }
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await this.client.delete<T>(endpoint);
    return response.data;
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await this.client.patch<T>(endpoint, data);
    return response.data;
  }

  /**
   * Upload a file using FormData
   * V17.28: Added for contract template image uploads
   */
  async uploadFile<T>(endpoint: string, formData: FormData): Promise<T> {
    const response = await this.client.post<T>(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

export const apiClient = new APIClient();
